// TW-0628 (2026-11-04) — THE PROTECTIONS A SHOP CAN OVERRULE, the form's half. Chris, settling the statute pass's product calls
// that evening: "I think we need a way for this and others to default to the thing but allow a shop to turn it off and accept
// the risk, somehow." · "We almost certainly shouldn't ever be stricter than any law, and to me the law is mostly about
// protecting our customers while giving them the freedom to overrule us and accept the risk."
//
// A protection is a refusal or a requirement the form applies (the registry is the repo root's form-protections.json). Every one
// is ON for every shop until its owner turns it off in the shop app's Settings → Waiver form → Protections, ticking "My shop
// accepts the responsibility." under the risk; the API keeps who and when on the shop's row and publishes one fact the form
// reads, `public_data.protections_off`: {<id>: the product's day it went off}. The API's signing door reads the same fact off the
// same row (services/waiver-api/consolidated_api/protections.py), so the two skip the same refusal: the door never refuses what
// this page allowed, nor signs what it refused.
//
// Turning a protection off REMOVES a refusal and writes no new words a client reads: the page simply stops saying no.
//
// The first (slice 1): every form since 2023 refused a yes to "Are you pregnant or nursing?" ("You cannot be pregnant or
// nursing."), in every state, though no state's rule turns a client away for it. Off, the yes signs and prints on the signed
// waiver, with the one line that says the shop's owner turned the refusal off and when.

export const PREGNANCY_REFUSAL = "pregnant_or_nursing_refusal";
// The first a state's rule applies (slice 2): the drugs-or-alcohol question a state's rule holds on (the contract's `not_intoxicated`).
// Off, the page does not ask it at all in that state, whatever the shop's own switch says (defaultQuestions.ts asksDefaultQuestion).
export const NOT_INTOXICATED = "not_intoxicated";
// The minors' two (slice 3), each read off the minors rules: the photo of the birth certificate or guardianship papers a rule asks of
// a parent (minors.ts asksGuardianProof), and the piercing spots a rule bars for anyone under 18 whatever a parent signs
// (minors.ts minorRefusal, `placementBarsOff`). Off, the page does not ask for the paper, and takes a parent's consent for the spot.
export const GUARDIAN_PROOF = "guardian_proof";
export const PLACEMENT_BANNED = "placement_banned";

/** Every protection this page knows how to honour, by its id in the registry: an id the page does not know turns nothing off. */
export const KNOWN_PROTECTIONS: readonly string[] = [PREGNANCY_REFUSAL, NOT_INTOXICATED, GUARDIAN_PROOF, PLACEMENT_BANNED];

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The published fact as the page keeps it: {id: day} for the ids it knows, each with a day; undefined for a shop that never
 *  turned one off (absent IS the default, the API's rule), or for anything that is not that shape. */
export function protectionsOff(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const out: Record<string, string> = {};
  for (const [id, day] of Object.entries(raw as Record<string, unknown>)) {
    if (KNOWN_PROTECTIONS.includes(id) && typeof day === "string" && DAY.test(day)) out[id] = day;
  }
  return Object.keys(out).length ? out : undefined;
}

/** Has this shop's owner turned this protection off? Read off the shop's public data, the static config's or the API's. */
export function protectionOff(publicData: { protections_off?: Record<string, string> } | null | undefined, id: string): boolean {
  return Boolean(publicData?.protections_off && Object.prototype.hasOwnProperty.call(publicData.protections_off, id));
}
